import React, {useState} from 'react';
import {FlatList, Text, View} from 'react-native';
import NoResults from 'src/common-components/noResults';
import {compose} from 'recompose';
import {withObservables} from '@nozbe/watermelondb/react';
import {withDatabase} from '@nozbe/watermelondb/react';
import {useDatabase} from '@nozbe/watermelondb/react';
import {Q} from '@nozbe/watermelondb';
import {useStyle} from 'src/providers/style';
import withState from 'src/redux/wrapper';
import {fuzzy} from 'src/common-utils/fuzzy';
import {mergeMap, of} from 'rxjs';
import DeleteModal from 'src/design-system/delete-modal';
import {useTranslations} from 'src/providers/translation';
import {BaseIndexScreen, Modal} from 'src/design-system';
import {Searchbar} from 'react-native-paper';
import {useSelector} from 'react-redux';
import {EventCategory, Role} from 'src/models';
import EventCategoryListItem from '../event-categories-item';
import EventCategoryForm from '../event-categories-form';
import {Typography} from 'src/styles';

interface Props {
  eventCategories: any[];
  role: Role;
}

const EventCategoriesTab = ({role, eventCategories}: Props) => {
  const database = useDatabase();
  const translations = useTranslations();
  const styles = useStyle();

  const {selectedGroup, userId} = useSelector(state => state.authentication);

  const [searchFilter, setSearchFilter] = useState<string>('');
  const [show, setShow] = useState(false);
  const [showDelete, setShowDelete] = useState<string | boolean>(false);
  const [eventCategoryState, setEventCategoryState] = useState(null);

  const title = eventCategoryState
    ? 'Edit Event Category'
    : 'Create Event Category';

  const searchedItems = eventCategories?.filter((category: any) => {
    return fuzzy(category?.name, searchFilter);
  });

  const addCallback = () => {
    setEventCategoryState(null);
    setShow(true);
  };

  const onSubmit = async (values: any) => {
    if (eventCategoryState) {
      await eventCategoryState.updateEntity({
        name: values.name,
        billable: values.billable,
      });
    } else {
      await database.write(async () => {
        return await database.get(EventCategory.table).create(entity => {
          entity.partition = selectedGroup;
          entity.name = values.name;
          entity.billable = values.billable;

          entity.createdBy = userId;
          entity.updatedBy = userId;
        });
      });
    }
    setShow(false);
  };

  const deleteCancelled = () => {
    setShowDelete(false);
  };
  const deleteConfirmed = async (item: any) => {
    item.delete();
    setShowDelete(false);
  };
  const editCallBack = (item: any) => {
    setEventCategoryState(item);
    setShow(true);
  };

  return (
    <BaseIndexScreen
      title={'Event Categories'}
      search={
        <Searchbar
          value={searchFilter}
          onChangeText={setSearchFilter}
          placeholder={translations('search')}
        />
      }
      add={addCallback}
      canAdd={role?.organizationLocationCreate || true}>
      <View
        style={[
          styles.borderRadiusM,
          styles.marginHorizontal,
          styles.padding,
          styles.backgroundColorSecondary400,
        ]}>
        <View
          style={[
            styles.row,
            styles.justifySpaceAround,
            styles.alignSelfStart,
          ]}>
          <Text style={[Typography.P3]}>
            Add or edit different non-clinical event categories that may be used
            for scheduling and timesheet purposes. Toggle whether or not to
            display Event on the Timesheet.
          </Text>
        </View>
      </View>
      <DeleteModal
        model={'Event Category'}
        text={showDelete ? `${showDelete?.name}` : null}
        show={[showDelete, setShowDelete]}
        cancelled={deleteCancelled}
        handleSubmit={() => deleteConfirmed(showDelete)}
      />
      <Modal show={[show, setShow]} title={title}>
        <EventCategoryForm
          eventCategory={eventCategoryState}
          onAssign={onSubmit}
        />
      </Modal>
      {searchedItems?.length === 0 ? (
        <NoResults
          iconName="account-supervisor"
          message={'No Event Categories Found'}
        />
      ) : (
        <FlatList
          data={searchedItems}
          scrollEnabled={true}
          keyExtractor={item => item?.id?.toString()}
          renderItem={({item}) => {
            return (
              <EventCategoryListItem
                eventCategory={item}
                canDelete={role?.organizationLocationDelete || true}
                canEdit={role?.organizationLocationEdit || true}
                deleteCallback={setShowDelete}
                editCallBack={editCallBack}
              />
            );
          }}
          style={[styles.flex]}
        />
      )}
    </BaseIndexScreen>
  );
};

export default compose(
  withDatabase,
  withState,
  withObservables(['authentication'], ({database, authentication}: any) => ({
    profile: authentication.userId
      ? database.get('users').findAndObserve(authentication.userId)
      : of({}),
  })),
  withObservables([], ({profile}: any) => {
    return {
      role: profile.role,
    };
  }),
  withObservables([], ({database, authentication}: any) => {
    return {
      instance: database
        ?.get('instances')
        .query(Q.where('_partition', authentication.selectedGroup))
        .observe()
        .pipe(mergeMap(x => x)),
    };
  }),
  withObservables(['instance'], ({database, authentication}: any) => ({
    eventCategories: database
      .get(EventCategory.table)
      .query(
        Q.where('deleted_at', null),
        Q.where('_partition', authentication.selectedGroup),
      ),
  })),
)(EventCategoriesTab);
